import { useTranslation } from "react-i18next";
import { Users, Store, Building2 } from "lucide-react";
import { StatCard, WalletHeroCard } from "@/components/common";
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";
import type { PlatformStats } from "@/services/adminService";

interface AdminDashboardStatsProps {
  stats: PlatformStats;
}

/** Platform KPIs: balance hero + catalog totals (alerts live in the attention panel). */
export function AdminDashboardStats({ stats }: AdminDashboardStatsProps) {
  const { t } = useTranslation("dashboard");

  const hasAnyCatalogCard =
    stats.total_wallet_balance != null ||
    stats.total_sellers != null ||
    stats.total_activities != null ||
    stats.total_houses != null;

  const smallCardCount = [
    stats.total_sellers,
    stats.total_activities,
    stats.total_houses,
  ].filter((value) => value != null).length;

  if (!hasAnyCatalogCard) {
    return null;
  }

  // Odd number of small cards: last one spans the row below xl (no orphan tile).
  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-5",
        smallCardCount % 2 === 1 && "[&>*:last-child]:max-xl:col-span-2",
      )}
    >
      {stats.total_wallet_balance != null ? (
        <WalletHeroCard
          className="col-span-2"
          label={t("admin.statTotalPlatformBalance")}
          amount={formatMoney(Math.abs(stats.total_wallet_balance))}
          isAmountOwed={stats.total_wallet_balance < 0}
        />
      ) : null}

      {stats.total_sellers != null ? (
        <StatCard
          title={t("admin.statTotalSellers")}
          value={stats.total_sellers}
          icon={Users}
          iconColor="primary"
          to="/admin/users"
          subtitle={t("admin.statActiveSellers", {
            count: stats.active_sellers ?? 0,
          })}
        />
      ) : null}

      {stats.total_activities != null ? (
        <StatCard
          title={t("admin.statTotalActivities")}
          value={stats.total_activities}
          icon={Store}
          iconColor="accent"
          to="/admin/activities"
          subtitle={[
            t("admin.statActiveActivities", {
              count: stats.active_activities ?? 0,
            }),
            ...(stats.total_restaurants != null && stats.total_services != null
              ? [
                  t("admin.statActivitiesBreakdown", {
                    restaurants: stats.total_restaurants,
                    services: stats.total_services,
                  }),
                ]
              : []),
          ].join(" · ")}
        />
      ) : null}

      {stats.total_houses != null ? (
        <StatCard
          title={t("admin.statTotalHouses")}
          value={stats.total_houses}
          icon={Building2}
          iconColor="accent"
          to="/admin/structures"
          subtitle={t("admin.statActiveHouses", {
            count: stats.active_houses ?? 0,
          })}
        />
      ) : null}

    </div>
  );
}
